/**
 * AccessCodeDetail — full code info shown inside the Update Status modal.
 *
 * Phase 2 (Gate Codes). Renders saved codes for one stop as text lines inside
 * the "Delivering to" block, with a REPORT CODE button to the right.
 *
 * Layout per shop-owner spec (May 15, 2026):
 *   ┌──────────────────────────────────────────────┬──────────────┐
 *   │ 🔑 Code 1: 7890 | buzz at the main gatehouse │              │
 *   │ 🔑 Code 2: AB42# | lobby door — press #...   │  REPORT      │
 *   │                                              │   CODE       │
 *   └──────────────────────────────────────────────┴──────────────┘
 *
 * Button is two lines tall, about twice the width of one button line —
 * gives the eye a clear, contained target without competing with Confirm
 * Delivery. Codes wrap within their column; instructions never flow under
 * the button area.
 *
 * Reported codes (deprecated via the report sheet) stay visible with
 * line-through + reduced opacity so the driver can still reference any
 * useful instructions while figuring out a workaround.
 *
 * Props:
 *   orderId: string — the order whose place_id we should match against.
 */
import React, { useState } from 'react';
import { KeyRound, UserRound } from 'lucide-react';
import { useAccessCodeLookup } from '@/hooks/useAccessCodeLookup';
import { useAuth } from '@/context/AuthContext';
import AccessCodeLookupStatus from '@/components/AccessCodeLookupStatus';
import { Button } from '@/components/ui/button';
import ReportCodeSheet from './ReportCodeSheet';

function AccessCodeDetailOpening({ orderId, openingId, addressKey, disabled = false }) {
  const lookup = useAccessCodeLookup({ orderId, openingId, addressKey, retainReported: true });
  const { codes, guardGated, reportedCodeIds, markReported, status } = lookup;
  const [reportOpen, setReportOpen] = useState(false);
  if (!orderId || (status === 'ready' && !codes.length && !guardGated && !reportOpen)) return null;

  const sortedCodes = [...codes].sort((a, b) => {
    if (a.type === b.type) return 0;
    return a.type === 'gate' ? -1 : 1;
  });

  const allReported = sortedCodes.every((c) => reportedCodeIds.has(c.id));

  return (
    <div className="mt-2" data-testid={`access-code-detail-${orderId}`}>
      <AccessCodeLookupStatus lookup={lookup} testId={`access-code-detail-status-${orderId}`} />
      {guardGated && (
        <div
          className="flex items-center gap-1.5 text-sm text-stone-700 mb-1"
          data-testid={`access-code-detail-guard-${orderId}`}
        >
          <UserRound className="inline h-4 w-4 text-amber-700 flex-shrink-0" />
          <span className="font-medium">Guard</span>
        </div>
      )}
      {sortedCodes.length > 0 && (
      <div className="flex items-start gap-3">
        {/* Left column: codes (flexible width) */}
        <div className="flex-1 min-w-0 space-y-1">
          {sortedCodes.map((code, idx) => {
            const label = sortedCodes.length > 1 ? `Code ${idx + 1}` : 'Code';
            const hasInstructions = Boolean(code.instructions && code.instructions.trim());
            const reported = reportedCodeIds.has(code.id);
            return (
              <div
                key={code.id}
                className={`text-sm text-stone-700 ${reported ? 'line-through opacity-50' : ''}`}
                data-testid={`access-code-detail-row-${code.id}`}
              >
                <KeyRound className="inline h-4 w-4 text-amber-700 mr-1.5 -mt-0.5 align-text-bottom" />
                <span className="text-stone-600">{label}:</span>{' '}
                <span className="font-mono font-bold text-stone-900 tracking-wider">
                  {code.code}
                </span>
                {hasInstructions && (
                  <>
                    <span className="text-stone-400 mx-1.5">|</span>
                    <span className="text-stone-700">{code.instructions}</span>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Right column: REPORT CODE button (fixed width, 2 lines tall) */}
        <Button
          type="button"
          onClick={() => setReportOpen(true)}
          disabled={allReported || disabled || status === 'retired'}
          variant="outline"
          className="flex-shrink-0 h-12 w-24 text-xs leading-tight whitespace-normal text-red-700 border-red-300 hover:bg-red-50 hover:text-red-800 disabled:opacity-40"
          data-testid={`report-code-button-${orderId}`}
        >
          REPORT
          <br />
          CODE
        </Button>
      </div>
      )}

      {status !== 'retired' && <ReportCodeSheet
        open={reportOpen}
        onOpenChange={setReportOpen}
        codes={sortedCodes}
        reportedCodeIds={reportedCodeIds}
        orderId={orderId}
        onReported={markReported}
        openingId={openingId}
        disabled={disabled}
      />}
    </div>
  );
}

export default function AccessCodeDetail(props) {
  const { sessionBinding } = useAuth() || {};
  // A new order/opening retires only this access/report view, never the parent
  // delivery form. Account replacement also discards the old report draft.
  // Lookup refreshes keep this identity and report draft intact.
  return <AccessCodeDetailOpening key={JSON.stringify([props.orderId, props.openingId, props.addressKey,
    sessionBinding?.revision, sessionBinding?.source, sessionBinding?.user?.tenant_id, sessionBinding?.user?.id])} {...props} />;
}
